import { useForm, router } from '@inertiajs/react'
import { NotaryPortalLayout } from '@/components/layout/NotaryPortalLayout'
import { DetailGrid } from '@/components/backoffice/DetailGrid'
import { FormSection } from '@/components/backoffice/FormSection'
import { SectionHeader } from '@/components/backoffice/SectionHeader'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Check, ExternalLink, FileText } from 'lucide-react'
import { DocumentAttachment, NotarizationAct, NotarizationSession, PortalNotarization } from '@/types'

type Props = {
  notarization: PortalNotarization
}

const ACT_TYPE_OPTIONS = [
  { value: 'acknowledgment', label: 'Acknowledgment' },
  { value: 'jurat', label: 'Jurat' },
  { value: 'copy_certification', label: 'Copy Certification' },
  { value: 'oath_affirmation', label: 'Oath / Affirmation' },
]

const stageVariant: Record<string, 'default' | 'secondary' | 'outline'> = {
  application: 'outline',
  payment: 'outline',
  booking: 'secondary',
  session: 'secondary',
  completed: 'default',
}

function formatDateTime(iso: string) {
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

function ActCard({ act }: { act: NotarizationAct }) {
  return (
    <div className="border rounded-lg p-3 bg-muted/20">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Check className="h-4 w-4 text-green-600" />
          <span className="text-sm font-medium">{act.document_name}</span>
        </div>
        <Badge variant="outline" className="text-[11px] capitalize font-normal">
          {act.act_type.replace(/_/g, ' ')}
        </Badge>
      </div>
      <div className="grid grid-cols-2 gap-2 text-[12px] text-muted-foreground">
        <span>Seal: {act.seal_number}</span>
        <span>Certificate: {act.certificate_number}</span>
        <span>Notarized: {formatDateTime(act.notarized_at)}</span>
        {act.certificate_url && (
          <a href={act.certificate_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline flex items-center gap-1">
            View Certificate <ExternalLink className="h-3 w-3" />
          </a>
        )}
      </div>
      {act.notes && <p className="text-[12px] text-muted-foreground mt-1">{act.notes}</p>}
    </div>
  )
}

function ActForm({ notarizationId, documentId, documentName }: { notarizationId: string; documentId: string; documentName: string }) {
  const { data, setData, post, processing, errors } = useForm({
    document_id: documentId,
    seal_number: '',
    certificate_number: '',
    act_type: 'acknowledgment',
    notarized_at: new Date().toISOString().slice(0, 16),
    notes: '',
    certificate: null as File | null,
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    post(`/notary_portal/notarizations/${notarizationId}/acts`, {
      data: { notarization_act: data },
      forceFormData: true,
    })
  }

  return (
    <form onSubmit={handleSubmit} className="border rounded-lg p-4 space-y-4">
      <div className="flex items-center gap-2 mb-2">
        <FileText className="h-4 w-4 text-muted-foreground" />
        <span className="text-sm font-medium">{documentName}</span>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <label className="text-[12px] font-medium text-muted-foreground uppercase tracking-wider">Seal Number</label>
          <Input className="h-8 text-sm" value={data.seal_number} onChange={(e) => setData('seal_number', e.target.value)} />
          {errors.seal_number && <p className="text-[12px] text-destructive">{errors.seal_number}</p>}
        </div>
        <div className="space-y-1.5">
          <label className="text-[12px] font-medium text-muted-foreground uppercase tracking-wider">Certificate Number</label>
          <Input className="h-8 text-sm" value={data.certificate_number} onChange={(e) => setData('certificate_number', e.target.value)} />
          {errors.certificate_number && <p className="text-[12px] text-destructive">{errors.certificate_number}</p>}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <label className="text-[12px] font-medium text-muted-foreground uppercase tracking-wider">Act Type</label>
          <Select value={data.act_type} onValueChange={(v) => setData('act_type', v)}>
            <SelectTrigger className="h-8 text-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {ACT_TYPE_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {errors.act_type && <p className="text-[12px] text-destructive">{errors.act_type}</p>}
        </div>
        <div className="space-y-1.5">
          <label className="text-[12px] font-medium text-muted-foreground uppercase tracking-wider">Notarized At</label>
          <Input className="h-8 text-sm" type="datetime-local" value={data.notarized_at} onChange={(e) => setData('notarized_at', e.target.value)} />
          {errors.notarized_at && <p className="text-[12px] text-destructive">{errors.notarized_at}</p>}
        </div>
      </div>

      <div className="space-y-1.5">
        <label className="text-[12px] font-medium text-muted-foreground uppercase tracking-wider">Notes (optional)</label>
        <Textarea className="text-sm min-h-15" value={data.notes} onChange={(e) => setData('notes', e.target.value)} />
      </div>

      <div className="space-y-1.5">
        <label className="text-[12px] font-medium text-muted-foreground uppercase tracking-wider">Certificate File</label>
        <Input
          className="h-8 text-sm"
          type="file"
          onChange={(e) => setData('certificate', e.target.files?.[0] ?? null)}
        />
        {errors.certificate && <p className="text-[12px] text-destructive">{errors.certificate}</p>}
      </div>

      <Button type="submit" size="sm" disabled={processing} className="h-8 text-[13px]">
        Create Act
      </Button>
    </form>
  )
}

function VideoSessionSection({ notarizationId, session }: { notarizationId: string; session: NotarizationSession | null }) {
  const { data, setData, patch, processing } = useForm({
    meeting_url: session?.meeting_url || '',
  })

  const handleUpdateUrl = (e: React.FormEvent) => {
    e.preventDefault()
    patch(`/notary_portal/notarizations/${notarizationId}/session`, {
      data: { video_session: { meeting_url: data.meeting_url } },
    })
  }

  const handleStart = () => {
    router.post(`/notary_portal/notarizations/${notarizationId}/session/start`)
  }

  const handleEnd = () => {
    if (!confirm('End this video session?')) return
    router.post(`/notary_portal/notarizations/${notarizationId}/session/end`)
  }

  return (
    <div className="space-y-4">
      <SectionHeader title="Video Session" />

      {session && (
        <DetailGrid
          columns={2}
          items={[
            { label: 'Provider', value: session.provider },
            {
              label: 'Status',
              value: session.ended_at
                ? 'Ended'
                : session.started_at
                  ? 'In Progress'
                  : 'Not Started',
            },
            ...(session.started_at ? [{ label: 'Started', value: formatDateTime(session.started_at) }] : []),
            ...(session.ended_at ? [{ label: 'Ended', value: formatDateTime(session.ended_at) }] : []),
          ]}
        />
      )}

      <form onSubmit={handleUpdateUrl} className="flex items-end gap-2">
        <div className="flex-1 space-y-1.5">
          <label className="text-[12px] font-medium text-muted-foreground uppercase tracking-wider">Meeting URL</label>
          <Input
            className="h-8 text-sm"
            type="url"
            value={data.meeting_url}
            onChange={(e) => setData('meeting_url', e.target.value)}
            placeholder="https://meet.example.com/..."
          />
        </div>
        <Button type="submit" size="sm" variant="outline" disabled={processing} className="h-8 text-[13px]">
          Update URL
        </Button>
      </form>

      {session?.meeting_url && (
        <a
          href={session.meeting_url}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 text-sm text-blue-600 hover:underline"
        >
          Open Meeting <ExternalLink className="h-3.5 w-3.5" />
        </a>
      )}

      <div className="flex gap-2">
        {!session?.started_at && (
          <Button size="sm" onClick={handleStart} className="h-8 text-[13px]">
            Start Session
          </Button>
        )}
        {session?.started_at && !session?.ended_at && (
          <Button size="sm" variant="outline" onClick={handleEnd} className="h-8 text-[13px]">
            End Session
          </Button>
        )}
      </div>
    </div>
  )
}

export default function NotarizationShow({ notarization }: Props) {
  const actedDocumentIds = new Set(notarization.acts.map((a) => a.document_id))

  const handleMarkComplete = () => {
    if (!confirm('Mark this notarization as complete? This cannot be undone.')) return
    router.post(`/notary_portal/notarizations/${notarization.id}/complete`)
  }

  return (
    <NotaryPortalLayout
      title="Notarization"
      breadcrumbs={[
        { label: 'Notary Portal', href: '/notary_portal/dashboard' },
        { label: 'Notarizations', href: '/notary_portal/notarizations' },
        { label: notarization.business_name },
      ]}
    >
      <div className="space-y-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-xl font-semibold tracking-tight">{notarization.business_name}</h1>
            <p className="text-sm text-muted-foreground">{notarization.type_name} &middot; {notarization.account_name}</p>
          </div>
          <Badge variant={stageVariant[notarization.stage] || 'outline'} className="text-[11px] capitalize font-normal">
            {notarization.stage.replace(/_/g, ' ')}
          </Badge>
        </div>

        <DetailGrid
          columns={2}
          items={[
            { label: 'Session Mode', value: (
              <Badge variant="outline" className="text-[11px] capitalize font-normal">
                {notarization.session_mode.replace(/_/g, ' ')}
              </Badge>
            ) },
            { label: 'Stage', value: notarization.stage.replace(/_/g, ' ') },
            ...(notarization.appointment ? [
              { label: 'Appointment', value: formatDateTime(notarization.appointment.scheduled_at) },
              { label: 'Duration', value: `${notarization.appointment.duration_minutes} min` },
            ] : []),
          ]}
        />

        {/* Documents and Acts */}
        <div className="space-y-4">
          <SectionHeader
            title="Documents"
            count={notarization.application_documents.length}
          />

          {notarization.application_documents.length === 0 ? (
            <div className="py-8 text-center">
              <p className="text-sm text-muted-foreground">No documents uploaded yet.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {notarization.application_documents.map((doc) => {
                const existingAct = notarization.acts.find((a) => a.document_id === doc.id)
                if (existingAct) {
                  return <ActCard key={doc.id} act={existingAct} />
                }
                return (
                  <ActForm
                    key={doc.id}
                    notarizationId={notarization.id}
                    documentId={doc.id}
                    documentName={doc.name}
                  />
                )
              })}
            </div>
          )}
        </div>

        {/* Video Session */}
        <VideoSessionSection
          notarizationId={notarization.id}
          session={notarization.session}
        />

        {/* Mark Complete */}
        {!notarization.completed && (
          <div className="border-t border-border/40 pt-6">
            <Button size="sm" onClick={handleMarkComplete} className="h-8 text-[13px]">
              Mark Complete
            </Button>
            <p className="text-[12px] text-muted-foreground mt-2">
              Marking complete will finalize this notarization. Ensure all documents have been notarized.
            </p>
          </div>
        )}

        {notarization.completed && (
          <div className="border-t border-border/40 pt-6">
            <div className="flex items-center gap-2">
              <Check className="h-4 w-4 text-green-600" />
              <span className="text-sm font-medium text-green-700">Notarization Complete</span>
            </div>
          </div>
        )}
      </div>
    </NotaryPortalLayout>
  )
}
